import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Users, Mail, Send, Loader2, Search, CheckCircle, X, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLeads = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(res.data);
    } catch (err) {
      if (err.response?.status === 403) { toast.error('Admin access required'); navigate('/'); }
    } finally { setLoading(false); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedLeads);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedLeads(next);
  };

  const selectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/leads/email`, {
        lead_ids: sendToAll ? [] : Array.from(selectedLeads),
        subject: emailForm.subject,
        message: emailForm.message,
        send_to_all: sendToAll
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Email sent to ${res.data.sent} leads!`);
      setShowEmailForm(false);
      setEmailForm({ subject: '', message: '' });
      setSelectedLeads(new Set());
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally { setSending(false); }
  };

  const filteredLeads = leads.filter(l =>
    l.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.course_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unconverted = leads.filter(l => !l.converted).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-leads">
      <Navbar />
      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="-ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold text-slate-900">Lead Management</h1>
              <p className="text-slate-600">{leads.length} leads captured, {unconverted} unconverted</p>
            </div>
            <Button
              onClick={() => { setSendToAll(true); setShowEmailForm(true); }}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={unconverted === 0}
              data-testid="email-all-leads-btn"
            >
              <Mail className="w-4 h-4 mr-2" />Email All Unconverted ({unconverted})
            </Button>
          </div>

          {/* Search + Selected Actions */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="leads-search"
              />
            </div>
            {selectedLeads.size > 0 && (
              <Button
                variant="outline"
                onClick={() => { setSendToAll(false); setShowEmailForm(true); }}
                data-testid="email-selected-btn"
              >
                <Send className="w-4 h-4 mr-2" />Email Selected ({selectedLeads.size})
              </Button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : (
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">Leads ({filteredLeads.length})</CardTitle>
                  <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-leads">
                    {selectedLeads.size === filteredLeads.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {filteredLeads.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {filteredLeads.map((lead) => (
                      <div
                        key={lead.id}
                        className={`flex items-center gap-4 py-3 px-2 rounded-lg transition-colors ${selectedLeads.has(lead.id) ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                        data-testid={`lead-row-${lead.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLeads.has(lead.id)}
                          onChange={() => toggleSelect(lead.id)}
                          className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <UserPlus className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{lead.name}</span>
                            {lead.converted && <Badge className="bg-green-100 text-green-700 text-xs">Converted</Badge>}
                            {!lead.converted && lead.emails_sent > 0 && <Badge variant="outline" className="text-xs">Emailed {lead.emails_sent}x</Badge>}
                          </div>
                          <p className="text-sm text-slate-500 truncate">{lead.email}</p>
                        </div>
                        <div className="hidden md:block text-sm text-slate-500 max-w-[200px] truncate">
                          {lead.course_title || 'No course'}
                        </div>
                        <div className="text-xs text-slate-400">
                          {lead.discount_code && (
                            <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-mono text-xs">{lead.discount_code}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No leads captured yet</p>
                    <p className="text-sm text-slate-400 mt-1">Leads are captured when visitors register to preview courses</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Email Compose Modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="email-compose-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="font-heading font-semibold text-lg text-slate-900">
                {sendToAll ? `Email All Unconverted Leads (${unconverted})` : `Email ${selectedLeads.size} Selected Leads`}
              </h3>
              <button onClick={() => setShowEmailForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSendEmail} className="p-5 space-y-4">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder="e.g. Don't miss your 50% discount!"
                  required
                  data-testid="email-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Message</Label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  rows={5}
                  placeholder="Write your message... Each lead's name and discount code will be automatically included."
                  required
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  data-testid="email-message-input"
                />
              </div>
              <p className="text-xs text-slate-400">Each email will automatically include the lead's name and their unique 50% discount code.</p>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" type="button" onClick={() => setShowEmailForm(false)}>Cancel</Button>
                <Button type="submit" disabled={sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-email-submit">
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  {sending ? 'Sending...' : 'Send Email'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
